import { supabase } from "./supabase";

/**
 * Selectable Zuora targeting values.
 *
 * Both catalogs come from `zuora_entitlement_values`, which reads the SAME active-subscription
 * rows and applies the same normalisation as the filter predicates. Sourcing the dropdown from one
 * place and evaluating membership from another is how a UI ends up offering values that can never
 * match anything.
 *
 * "Plan" deliberately means `rate_plan_details[].productName` — the coverage name, including the
 * "- Secondary" variants. Zuora's `ratePlanName` holds billing constructs ("Sign-up Discount",
 * "Individual Plan Setup Fees") and carries no primary/secondary distinction, so it is not what an
 * operator means when they ask to target Emergency Shield.
 */
export type ZuoraEntitlementKind = "product" | "plan";

export interface ZuoraEntitlementValue {
  value: string;
  contact_count: number;
}

const cache = new Map<ZuoraEntitlementKind, ZuoraEntitlementValue[]>();

export const getZuoraEntitlementValues = async (
  kind: ZuoraEntitlementKind,
  opts: { force?: boolean } = {},
): Promise<ZuoraEntitlementValue[]> => {
  if (!opts.force && cache.has(kind)) {
    return cache.get(kind)!;
  }

  const { data, error } = await supabase.rpc("zuora_entitlement_values", {
    p_kind: kind,
    p_limit: 1000,
  });
  if (error) {
    throw error;
  }

  const rows = ((data ?? []) as ZuoraEntitlementValue[]).filter(
    (r) => typeof r.value === "string" && r.value.trim().length > 0,
  );
  cache.set(kind, rows);
  return rows;
};

/** Field key -> catalog kind. Keep in step with the compilers' field names. */
export const ZUORA_FIELD_KIND: Record<string, ZuoraEntitlementKind> = {
  product_names: "product",
  zuora_plan_name: "plan",
};

export const isZuoraEntitlementField = (field: string): boolean =>
  Object.prototype.hasOwnProperty.call(ZUORA_FIELD_KIND, field);
